import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { dashboardApi } from '../api/endpoints.js';
import { useApi } from '../hooks/useApi.js';
import { useAuth } from '../auth/AuthContext.jsx';
import PageHeader from '../components/PageHeader.jsx';
import StatCard from '../components/StatCard.jsx';
import Card from '../components/Card.jsx';
import Spinner from '../components/Spinner.jsx';
import Badge from '../components/Badge.jsx';
import EmptyState from '../components/EmptyState.jsx';
import { formatCurrency, formatDateTime } from '../utils/format.js';

/**
 * KPI selection per role - the API already scoped the numbers.
 *
 * No IT row: that team has its own screen (pages/it/ItDashboardPage.jsx) about
 * delivery rather than about the funnel, and listing pipeline value here for
 * them would only be a promise this page no longer keeps.
 */
const KPI_BY_ROLE = {
  TELEPRO: ['callsQualified', 'meetingsBooked', 'conversionRate', 'hotLeads', 'averageScore', 'appointmentsThisWeek'],
  COMMERCIAL: ['customers', 'leadsReceived', 'appointmentsUpcoming', 'won', 'winRate', 'pipelineValue'],
  AGENCY: ['customers', 'leads', 'hotLeads', 'appointmentsUpcoming', 'won', 'pipelineValue'],
  ADMIN: ['customers', 'leads', 'appointmentsUpcoming', 'activeUsers', 'unassignedCustomers', 'pipelineValue'],
};

const MONEY_KPIS = ['pipelineValue', 'wonValue'];
const PERCENT_KPIS = ['conversionRate', 'winRate'];

const DashboardPage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { data, loading, error } = useApi(() => dashboardApi.get(), []);

  if (loading) return <Spinner full />;
  if (error) return <EmptyState title={t('common.error')} description={error.message} />;

  const keys = KPI_BY_ROLE[user.role] ?? KPI_BY_ROLE.TELEPRO;

  const renderValue = (key) => {
    const value = data.kpis[key] ?? 0;
    if (MONEY_KPIS.includes(key)) return formatCurrency(value, i18n.resolvedLanguage);
    if (PERCENT_KPIS.includes(key)) return `${value}%`;
    return value;
  };

  return (
    <>
      <PageHeader
        title={t('dashboard.greeting', { name: user.firstName })}
        subtitle={t(`roles.${user.role}`)}
      />

      <div className="stat-grid">
        {keys.map((key) => (
          <StatCard key={key} label={t(`dashboard.${key}`)} value={renderValue(key)} />
        ))}
      </div>

      <div className="grid grid--2">
        <Card title={t('dashboard.nextAppointments')}>
          {data.nextAppointments.length ? (
            <ul className="timeline">
              {data.nextAppointments.map((appointment) => (
                <li key={appointment.id} className="timeline__item">
                  <div className="timeline__main">
                    <p className="timeline__title">{appointment.title}</p>
                    <p className="timeline__meta">
                      {formatDateTime(appointment.startAt, i18n.resolvedLanguage)}
                      {appointment.customerName ? ` - ${appointment.customerName}` : ''}
                      {appointment.commercialName ? ` - ${appointment.commercialName}` : ''}
                    </p>
                  </div>
                  <Badge value={appointment.status} label={t(`agenda.status.${appointment.status}`)} />
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title={t('agenda.noAppointments')} />
          )}
        </Card>

        <Card title={t('dashboard.customersByStatus')}>
          <ul className="bar-list">
            {Object.entries(data.charts.customersByStatus).map(([status, count]) => {
              const total = Object.values(data.charts.customersByStatus).reduce((a, b) => a + b, 0) || 1;
              return (
                <li key={status} className="bar-list__row">
                  <span className="bar-list__label">{t(`customers.status.${status}`, status)}</span>
                  <span className="bar-list__track">
                    <span className="bar-list__fill" style={{ width: `${(count / total) * 100}%` }} />
                  </span>
                  <span className="bar-list__value">{count}</span>
                </li>
              );
            })}
          </ul>
          {!Object.keys(data.charts.customersByStatus).length ? <EmptyState title={t('common.noData')} /> : null}
        </Card>
      </div>

      {user.role === 'TELEPRO' ? (
        <div className="cta-row">
          <Link className="btn btn--primary" to="/telepro/qualification">
            {t('leads.new')}
          </Link>
          <Link className="btn" to="/telepro/book">
            {t('agenda.bookMeeting')}
          </Link>
        </div>
      ) : null}
    </>
  );
};

export default DashboardPage;
